(ns practitest.frontend.components2.value-score
  (:require [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.components.grid.localstorage :as grid-storage]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.common.tooltip :as common-tooltip]
            [practitest.frontend.components2.buttons :as buttons]
            [practitest.frontend.components2.feedback :as feedback]
            [reagent.core :as reagent]))

(defn- round-float [num base]
  (let [divisor (js/Math.pow 10 base)]
    (/ (js/Math.round (* (float num) divisor))
       divisor)))

(defn value-score-field
  [score]
  ;; Score can be either string (because it's serialized as string from Ruby, to avoid
  ;; floating point errors and there's no correct decimal class in JSON)
  (let [score (if (number? score)
                score
                (fu/try-parse-float score 0))]
    (if (= score 0)
      [:span (get-translation "value-score.na" "NA")]
      ;; TODO: different classes (mouseUX ones ?)
      [:span {:class (cond
                       (< score 20)
                       "text-danger"
                       (< score 50)
                       "text-warning"
                       :else
                       "text-success")}
       (fu/fixed-point score 2)])))

(defn usual-score-row
  [score-data score-type label tooltip-text]
  (let [coeff (or (get-in score-data [:score :coefficients score-type])
                  0.0)
        score-value (or (get-in score-data [:score score-type])
                        0.0)
        ;; TODO: pass from backend, do not recalculate here
        score-adjusted (round-float (* coeff score-value) 2)]
    [tooltip/mux-tooltip
     {:trigger "click"}
     tooltip-text
     (fn [props]
       [:div.value-score-row
        props
        (if (<= score-adjusted 4)
          [icons/vs-icon-down]
          [icons/vs-icon-up])
        label])]))

(defn comments-score-row
  [data]
  (when (some? (get-in data [:score :comments-score]))
    [usual-score-row data :comments-score
     (get-translation "testset.tabs.comments" "Comments")
     (get-translation "value-score.factor.comments.tooltip" "A test that has recent comments receives a higher score")]))

(defn runs-score-row
  [data]
  (when (some? (get-in data [:score :runs-score]))
    [usual-score-row data :runs-score
     (get-translation "value-score.factor.status-fluctuations.label" "Status Fluctuations")
     (get-translation "value-score.factor.status-fluctuations.tooltip" "A test whose run status is changing in a controlled way, not randomly or due to flakiness, receives a higher score")]))

(defn suites-score-row
  [data]
  (when (some? (get-in data [:score :suites-score]))
    [usual-score-row data :suites-score
     (get-translation "value-score.factor.test-set-appearance.label" "Test Set Appearance")
     (get-translation "value-score.factor.test-set-appearance.tooltip" "A test that appears in multiple test sets receives a higher score")]))

(defn testers-score-row
  [data]
  (when (some? (get-in data [:score :testers-score]))
    [usual-score-row data :testers-score
     (get-translation "value-score.factor.multiple-executing-testers.label" "Multiple Executing Testers")
     (get-translation "value-score.factor.multiple-executing-testers.tooltip" "A test that has been executed by different testers receives a higher score")]))

(defn issues-score-row
  [data]
  (when (some? (get-in data [:score :issues-score]))
    [usual-score-row data :issues-score
     (get-translation "issue.linked" "Linked Issues")
     (get-translation "value-score.factor.linked-issues.tooltip" "A test that has more linked issues receives a higher score")]))

(defn steps-score-row
  [data]
  (when (some? (get-in data [:score :steps-score]))
    [usual-score-row data :steps-score
     (get-translation "generic.label.steps" "Steps")
     (get-translation "value-score.factor.steps.tooltip" "A test with several detailed and structured steps receives a higher score")]))

(defn value-score-na?
  "Checks if na? mode of a value score for a test"
  [value-score-data]
  (or (nil? value-score-data)
      (not= "ok" (:status value-score-data))))

(defn value-score-component [entity-type entity-id value-score-data]
  (reagent/with-let [local-state (reagent/atom {:collapsed? (grid-storage/get-section-collapsed entity-type "value-score" true)})]
    (let [na? (value-score-na? value-score-data)
          collapsed? (or (:collapsed? @local-state) na?)
          total-score (get-in value-score-data [:score :total-score])]
      [:div.fields-panel.value-score-panel
       [:div.header.value-score-header
        {:class (dom/classes-pairs-v :collapsed collapsed?)}
        [:div.value-score-header-row
         [buttons/icon-button
          {:icon (if collapsed?
                   [icons/mux-chevron-right]
                   [icons/mux-chevron-down2])
           :disabled? na?
           :mixpanel ["Value Score" {"Action" (if collapsed?
                                                "Expand the component"
                                                "Collapse the component")}]
           :on-click #(let [new-state (not (:collapsed? @local-state))]
                        (swap! local-state assoc :collapsed? new-state)
                        (grid-storage/set-section-collapsed entity-type "value-score" new-state))}]
         [icons/mux-smart-fox2]
         [:div.title (get-translation "value-score.title" "Value Score")]
         [common-tooltip/info-circle-tooltip
          [:span (get-translation "value-score.help.description" "Value Score grades each test by measuring how much value it adds to your project, based on its latest runs and any recent updates to the test itself. For more information, please refer to our ")
           [:a {:href "https://www.practitest.com/help/data-management-and-analysis/test-value-score/"
                :target "_blank"}
            (get-translation "generic.label.help_page" "help page")]]
          {:trigger "click" :class "clickable"}]
         [tooltip/mux-tooltip
          {:trigger "hover"}
          (get-translation "value-score.tooltip.min-runs-required" "At least two runs on different days are required to calculate Value Score")
          (fn [props]
            [:div.value-score-pill
             (cond-> {:class (cond
                               na? :na
                               (not (number? total-score)) :red
                               (>= total-score 70) :green
                               (<= total-score 10) :red
                               :else :yellow)}
                     na? (merge props))
             (if na?
               (get-translation "value-score.na" "NA")
               (fu/fixed-point total-score 2))])]]
        [feedback/feedback-component {:entity-type entity-type
                                      :entity-id entity-id
                                      :mixpanel-up ["Value Score" {"Action" "Thumbs Up"}]
                                      :mixpanel-down ["Value Score" {"Action" "Thumbs Down"}]
                                      :feature "value_score"}]]
       (when (and (not na?)
                  (not collapsed?))
         [:div.value-score-factors-panel
          [:div.value-score-factors-title (get-translation "value-score.factors.title" "Factors")]
          [:div.value-scores-section
           [runs-score-row value-score-data]
           [comments-score-row value-score-data]
           [suites-score-row value-score-data]
           [testers-score-row value-score-data]
           [issues-score-row value-score-data]
           [steps-score-row value-score-data]]])])))
